"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/stores";

/**
 * WorkspaceCacheGuard — tenant-isolation backstop for the React Query cache.
 *
 * WHY THIS EXISTS (2026-06-26):
 * Customer-data query keys are scoped by ApiMode (demo/live) but NOT by
 * workspaceId — see hooks/api/query-keys.ts (`groupKeys.all = ['groups', mode]`,
 * same for recipients/tags/custom-fields/…). So for a multi-workspace (MSSP)
 * tenant, Workspace A and Workspace B share the SAME cache slot in the same
 * mode. The TeamSwitcher works around this by calling `invalidateQueries()` on
 * switch, but invalidate ≠ evict: during the post-switch refetch the list
 * re-renders with the PREVIOUS workspace's cached rows under the new
 * workspace's header (a transient flash of the wrong tenant's data), and any
 * other code path that changes the active workspace WITHOUT the manual
 * invalidate (super-admin context switch, future call sites) would show stale
 * cross-tenant data persistently.
 *
 * This guard makes isolation UNFORGETTABLE: it subscribes to the workspace
 * store directly, so whenever the active workspace OR company actually changes
 * it EVICTS the entire query cache (`queryClient.clear()`). Eviction (not
 * invalidation) means the previous tenant's rows are physically gone — they can
 * never be served while the new tenant's data refetches; the UI shows its
 * loading skeletons instead. Because it keys off the store, no current or
 * future switch path can bypass it.
 *
 * It also resets persisted table FILTER/SEARCH state on the same tenant change
 * so a filter value typed against one tenant (e.g. a recipient name or a
 * department) cannot bleed into another tenant's table on the same browser.
 *
 * IMPORTANT — this is a TENANT-SWITCH reset, which is correct and expected. It
 * is NOT the rejected "auto-clear the active search to hide an empty result on
 * the SAME tenant" behavior — that stays the user's own action (via the
 * "Clear filters" affordance in the table's no-results state).
 *
 * Mounted once at the app shell (inside QueryProvider).
 */

const FILTER_KEY_SUFFIXES = [
  "-column-filters",
  "-global-filter",
  "-advanced-search",
] as const;

function resetPersistedTableFilters(): void {
  if (typeof window === "undefined") return;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && FILTER_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))) {
      toRemove.push(key);
    }
  }
  if (toRemove.length === 0) return;
  toRemove.forEach((key) => localStorage.removeItem(key));
  // Reset the global search box on any CURRENTLY-MOUNTED table: useTablePersistence
  // listens for "recipients-filter-change" and re-reads the (now-cleared)
  // `${prefix}-global-filter` key. Column filters reset on the table's next mount
  // (it reads the cleared keys then). The query cache is already evicted by the
  // caller, so the rendered data is correct regardless of any lingering chip.
  window.dispatchEvent(new Event("recipients-filter-change"));
}

export function WorkspaceCacheGuard() {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const companyId = useWorkspaceStore((s) => s.activeCompanyId);
  const prev = useRef<{ ws: string | null; co: string | null } | null>(null);

  useEffect(() => {
    const current = { ws: workspaceId, co: companyId };

    // First settled value (login / hydration / auto-select first workspace) is
    // not a tenant SWITCH — record it without evicting (cache is empty here
    // anyway, and evicting would cancel the very first in-flight queries).
    if (prev.current === null) {
      // Record the first settled tenant identity (workspace OR company — a
      // super-admin company-level view may have no active workspace) without
      // evicting: the cache is empty at hydration and evicting here would
      // cancel the very first in-flight queries.
      if (workspaceId || companyId) prev.current = current;
      return;
    }

    if (prev.current.ws === workspaceId && prev.current.co === companyId) {
      return;
    }

    prev.current = current;

    // Actual tenant change → evict ALL cached server data so the previous
    // tenant's rows can never render under the new tenant during refetch.
    queryClient.clear();
    // …and reset persisted table filters so filter values don't bleed across
    // tenants on this browser.
    resetPersistedTableFilters();
  }, [workspaceId, companyId, queryClient]);

  return null;
}
